/**
 * Deterministic selector-slug helpers shared by the RTS UI (which stamps
 * data-test attributes) and the E2E/unit tests (which target them). Keeping a
 * single source of truth means a label change cannot silently desync the DOM
 * selectors from the tests that assert on them.
 *
 * Slugs are derived from the RAW rtsRowMapping labels (code constants, NOT
 * translated FormattedMessage text), and retain the "(direction)" qualifier so
 * sibling rows stay unique (e.g. the three Inbound In-Transit rows).
 */

// Matches any run of characters that are NOT lowercase-alphanumeric; each run is
// collapsed to a single hyphen. Example: "In Transit (DS -> FC/Seller)" — the spaces,
// parens, ">", and "/" all match and become hyphens -> "in-transit-ds-fc-seller-".
const NON_SLUG_CHARS = /[^a-z0-9]+/g;
// Matches leading/trailing hyphens left over after the collapse, so they can be trimmed
// (e.g. the trailing "-" above -> "in-transit-ds-fc-seller").
const EDGE_HYPHENS = /^-+|-+$/g;

/** "In Transit (DS -> FC/Seller)" -> "in-transit-ds-fc-seller" */
export function rtsRowSlug(label) {
    if (!label) return "";
    // Locale-INDEPENDENT toLowerCase: toLocaleLowerCase would, e.g. in the Turkish
    // locale, map "I" -> "ı" (dotless), which then falls outside [a-z0-9] and is
    // stripped -- breaking the deterministic data-test contract with the E2E tests.
    return label.trim().toLowerCase().replace(NON_SLUG_CHARS, "-").replace(EDGE_HYPHENS, "");
}

/** data-test value for a top-level RTS section row, e.g. "section-inbound" */
export function sectionTestId(label) {
    return `section-${rtsRowSlug(label)}`;
}

/** data-test value for a nested sub-section header row, e.g. "subsection-..." */
export function subsectionTestId(label) {
    return `subsection-${rtsRowSlug(label)}`;
}

/** data-test value for a leaf status row, e.g. "row-delivery-cancelled" */
export function rowTestId(label) {
    return `row-${rtsRowSlug(label)}`;
}

/**
 * react-intl message id for an RTS waterfall label, e.g.
 *   "Reverse Inducted — Reattemptable" -> "commons.reverse_inducted_—_reattemptable"
 * matching the keys in src/components/il8n/locales/*.json.
 *
 * Single source of truth for the id derivation that RTSDataUI renders, so the
 * derived id cannot silently desync from the locale keys. Unlike rtsRowSlug this
 * PRESERVES punctuation (the em-dash, parens) — only spaces become underscores —
 * because that is exactly how the locale keys are spelled.
 *
 * Locale-INDEPENDENT toLowerCase is mandatory: toLocaleLowerCase() would, in the
 * Turkish locale, map "I" -> "ı" (dotless i), deriving "commons.reverse_ınducted_*"
 * which is NOT a locale key — silently falling back to the English defaultMessage.
 */
export function rtsMessageId(label) {
    if (!label) return "commons.";
    return "commons." + label.toLowerCase().split(" ").join("_");
}



// WEBPACK FOOTER //
// ./src/commons/utils/testSelectors.js